import { useEffect, useState } from 'react';
import '../ori-landing.css';
import ContactLink from './ContactLink';
import AccountMenu from './AccountMenu';
import { buildSmsHref } from '../../../shared-web/onboarding-phone';

type OriLandingProps = {
  onMessageOri: () => void;
};

type Me = { self_name: string; ori_number: string };

const ORI_VIDEO =
  'https://res.cloudinary.com/oriscen-app-prod/video/upload/f_auto,q_auto,w_900,c_limit/v1781915684/Ori_Video_2_mzltax.mp4';
const ORI_POSTER =
  'https://res.cloudinary.com/oriscen-app-prod/video/upload/f_auto,q_auto,w_900,c_limit,so_0/v1781915684/Ori_Video_2_mzltax.jpg';

const steps = [
  {
    number: '01',
    label: 'Ask',
    title: 'Start with one message.',
    copy: 'Tell Ori who you want to see, roughly when, and the kind of plan you have in mind.',
    source: 'iMessage',
    image: '/ori-imessage-chat.png',
    alt: 'Jackson asks Ori to coordinate a casual dinner with Riley',
    className: 'flow-proof-chat',
  },
  {
    number: '02',
    label: 'Coordinate',
    title: 'Ori finds the overlap.',
    copy: 'Ori checks availability, follows up separately, and brings back a time that works.',
    source: 'Calendar',
    image: '/ori-calendar-invite.png',
    alt: 'A calendar invitation for dinner with Riley at 7:30 PM',
    className: 'flow-proof-calendar',
  },
  {
    number: '03',
    label: 'Go',
    title: 'One plan. Ready to use.',
    copy: 'Everyone gets the same time, place, route, and updates—without another group chat.',
    source: 'Ori Plan',
    image: '/ori-riley-plan.png',
    alt: 'Riley’s Ori plan with three walkable stops in San Francisco',
    className: 'flow-proof-plan',
  },
];

const situations = [
  {
    prompt: 'See if Riley wants to grab dinner next week. Somewhere casual.',
    meta: 'Catch up',
  },
  {
    prompt: 'Get everyone together for Maya’s birthday. She wants dancing, not a big dinner.',
    meta: 'Birthday',
  },
  {
    prompt: 'Find a night for the four of us before Ben leaves town.',
    meta: 'Group plan',
  },
  {
    prompt: 'Plan something low-key after work on Thursday, close to the office.',
    meta: 'Tonight',
  },
];

const faqs = [
  {
    question: 'Do my friends need to download Ori?',
    answer: 'No. Friends can respond from the conversation or link you share with them. They do not need another app to participate in the plan.',
  },
  {
    question: 'Can Ori message people for me?',
    answer: 'Ori coordinates only with the people you choose for the plan. You remain in control of who is invited and can change or stop the plan.',
  },
  {
    question: 'Does Ori need access to my calendar?',
    answer: 'Calendar access is optional. Connecting it helps Ori find open times faster, and you can disconnect it at any time.',
  },
  {
    question: 'Can I change a plan after it is made?',
    answer: 'Yes. Tell Ori what changed. Ori keeps the plan and its participants aligned around the latest details.',
  },
  {
    question: 'Does Ori book or pay for things automatically?',
    answer: 'No purchase or reservation is made without your confirmation. Ori can prepare the plan, but you decide before anything consequential happens.',
  },
];

function Logo() {
  return (
    <img
      className="product-logo"
      src="/figma/logo-orange.svg"
      alt="Ori"
    />
  );
}

function Cta({
  onClick,
  secondary = false,
  children,
}: {
  onClick: () => void;
  secondary?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button className={`ori-cta${secondary ? ' secondary' : ''}`} type="button" onClick={onClick}>
      <span>{children}</span>
      <span aria-hidden="true">↗</span>
    </button>
  );
}

export default function OriLanding({ onMessageOri }: OriLandingProps) {
  // Login state drives the nav (Sign in → name pill + Log out) and the "Text
  // Ori" CTAs. ori_session is HttpOnly so JS can't read it — /api/me is the
  // only client-visible signal. Anonymous visitors just leave me = null.
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/me', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.ok) {
          setMe({ self_name: data.self_name || '', ori_number: String(data.ori_number || '').trim() });
        }
      })
      .catch(() => { /* anonymous — leave me = null */ });
    return () => { cancelled = true; };
  }, []);

  const logout = async () => {
    try {
      await fetch('/api/web-session/logout', { method: 'POST', credentials: 'same-origin' });
    } catch {
      /* best-effort — reload anyway */
    }
    window.location.href = '/';
  };

  // Signed-in visitors already have an Ori thread — "Text Ori" jumps straight
  // into it (sms:) instead of re-running the onboarding modal. Anonymous (or a
  // signed-in user whose Ori line we couldn't resolve) still gets the modal.
  const handleTextOri = () => {
    if (me?.ori_number) {
      window.location.href = buildSmsHref(me.ori_number, '');
      return;
    }
    onMessageOri();
  };

  const firstName = (me?.self_name || '').trim().split(/\s+/)[0] || '';

  return (
    <div className="ori-product-site">
      <header className="product-nav">
        <a href="/" aria-label="Ori home"><Logo /></a>
        <nav aria-label="Primary navigation">
          <a href="#how">How it works</a>
          <a href="#situations">Ways to use Ori</a>
          <a href="#questions">Questions</a>
        </nav>
        {me ? (
          <div className="product-nav-account">
            <AccountMenu firstName={firstName} onLogout={logout} />
          </div>
        ) : (
          <Cta onClick={onMessageOri}>Sign in</Cta>
        )}
      </header>

      <main>
        <section className="product-hero">
          <div className="hero-copy hero-copy-decision">
            <p className="section-kicker">Your social life, coordinated</p>
            <h1>
              Don’t let a 30-second decision<br />
              <em>take half an hour.</em>
            </h1>
            <p className="hero-definition">
              Ori is the AI assistant in your messages. Share her tastes, habits,
              preferences, and must-haves, and Ori quickly finds the right option—an
              activity, restaurant, hotel, and more. It works just as well for them,
              or for you.
            </p>
            <div className="hero-actions">
              <Cta onClick={handleTextOri}>Text Ori</Cta>
              <span>Opens Messages. Friends need no app.</span>
            </div>
          </div>
          <div className="hero-video">
            <video
              src={ORI_VIDEO}
              poster={ORI_POSTER}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="Ori coordinating a social plan in Messages"
            />
          </div>
        </section>

        <section className="flow-section" id="how">
          <div className="section-intro">
            <p className="section-kicker">How it works</p>
            <h2>From “we should”<br />to a plan you can use.</h2>
            <p>
              One request starts the process. Ori handles the coordination and
              keeps the result connected.
            </p>
          </div>
          <div className="flow-track">
            {steps.map((step, index) => (
              <article className="flow-step" key={step.number}>
                <header>
                  <span><i>{step.number}</i>{step.label}</span>
                  <small>{step.source}</small>
                </header>
                <div className={`flow-proof ${step.className}`}>
                  <img src={step.image} alt={step.alt} />
                </div>
                <div className="flow-step-copy">
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                </div>
                {index < steps.length - 1 && <span className="flow-connector" aria-hidden="true">→</span>}
              </article>
            ))}
          </div>
          <p className="mobile-swipe-note">Swipe to follow the plan <span>→</span></p>
        </section>

        <section className="closing-section">
          <p className="section-kicker">One message away</p>
          <h2>Who have you been<br />meaning to see?</h2>
          <p>Tell Ori. We’ll take it from there.</p>
          <Cta onClick={handleTextOri}>Text Ori</Cta>
        </section>

        <section className="situations-section" id="situations">
          <div className="situations-copy">
            <p className="section-kicker">Start naturally</p>
            <h2>Ask the way you would ask a friend.</h2>
            <p>
              You do not need a form, perfect details, or a finished idea.
              Give Ori the intention. The plan can take shape from there.
            </p>
          </div>
          <div className="situation-messages">
            {situations.map((situation, index) => (
              <article key={situation.prompt} className={`situation-message situation-message-${index + 1}`}>
                <span>{situation.meta}</span>
                <p>{situation.prompt}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="control-section">
          <div className="control-heading">
            <p className="section-kicker">You stay in control</p>
            <h2>Helpful with the details.<br />Quiet when it matters.</h2>
          </div>
          <div className="control-points">
            <article>
              <span>01</span>
              <h3>You choose the people.</h3>
              <p>Ori contacts only the people you include in the plan.</p>
            </article>
            <article>
              <span>02</span>
              <h3>Friends join as they are.</h3>
              <p>No new app is required for someone to respond or follow the plan.</p>
            </article>
            <article>
              <span>03</span>
              <h3>You confirm real actions.</h3>
              <p>Nothing is booked or paid for without your confirmation.</p>
            </article>
            <article>
              <span>04</span>
              <h3>Your plan stays current.</h3>
              <p>Changes flow back to one source of truth for everyone involved.</p>
            </article>
          </div>
        </section>

        <section className="questions-section" id="questions">
          <div className="questions-heading">
            <p className="section-kicker">Good to know</p>
            <h2>A few questions<br />before you text.</h2>
          </div>
          <div className="questions-list">
            {faqs.map((faq, index) => (
              <details key={faq.question}>
                <summary>
                  <span><i>{String(index + 1).padStart(2, '0')}</i>{faq.question}</span>
                  <b aria-hidden="true">+</b>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

      </main>

      <footer className="product-footer">
        <a href="/" aria-label="Ori home"><Logo /></a>
        <nav>
          <a href="#how">How it works</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <ContactLink label="Contact" />
        </nav>
        <span>© 2026 Oriscen Inc.</span>
      </footer>
    </div>
  );
}
